import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MdAdd, MdError } from 'react-icons/md';
import RecordCard from '../../components/StudentRecords/RecordCard';
import CreateRecordModal from '../../components/StudentRecords/CreateRecordModal';
import AddFollowUpModal from '../../components/StudentRecords/AddFollowUpModal';
import RecordFilters from '../../components/StudentRecords/RecordFilters';
import { useStudentRecords } from '../../hooks/useStudentRecords';
import { getMyStudents } from '../../services/enrollmentService';
import useAuth from '../../hooks/useAuth';
import useClubVocab from '../../hooks/useClubVocab';

// Normalize trainer's class-based student structure
const normalizeTrainerStudents = (data) => {
  if (!data) return [];
  const allStudents = [];
  if (data?.classes && Array.isArray(data.classes)) {
    data.classes.forEach(cls => {
      if (cls.students && Array.isArray(cls.students)) {
        allStudents.push(...cls.students);
      }
    });
  }
  return allStudents;
};

function StudentRecords() {
  const { user } = useAuth();
  const v = useClubVocab();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpRecordId, setFollowUpRecordId] = useState(null);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [filters, setFilters] = useState({
    studentId: '',
    recordType: '',
    category: '',
    severity: '',
    createdBy: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: 20,
  });

  // Get trainer's students (getMyStudents returns { classes: [...] }, flatten to students)
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ['my-students', user?._id],
    queryFn: async () => {
      const response = await getMyStudents();
      const data = response.data;
      console.log('[TrainerStudentRecords] Raw API response:', data);
      // Flatten students from classes structure
      const normalized = normalizeTrainerStudents(data);
      console.log('[TrainerStudentRecords] Normalized students:', normalized);
      return { students: normalized };
    },
    enabled: !!user?._id,
  });

  const students = studentsData?.students || [];

  // Get records
  const { data: recordsData, isLoading: recordsLoading, error: recordsError } = useStudentRecords(filters);

  const handleToggleExpand = useCallback((recordId) => {
    setExpandedRecords(prev => ({
      ...prev,
      [recordId]: !prev[recordId],
    }));
  }, []);

  const handleAddFollowUp = (recordId) => {
    setFollowUpRecordId(recordId);
    setShowFollowUpModal(true);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const records = recordsData?.records || [];
  const total = recordsData?.total || 0;
  const pages = recordsData?.pages || 1;
  const currentPage = filters.page;

  const hasActiveFilters = [
    filters.studentId,
    filters.recordType,
    filters.category,
    filters.severity,
    filters.createdBy,
    filters.dateFrom,
    filters.dateTo,
  ].filter(Boolean).length > 0;

  // Single source of truth for clearing filters — shared by the filter bar's
  // "Clear all" and the empty-state "Clear filters" so they behave identically.
  const clearFilters = () => setFilters({
    studentId: '', recordType: '', category: '', severity: '',
    createdBy: '', dateFrom: '', dateTo: '', page: 1, limit: 20,
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900/50">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div>
              <h1 className="text-2xl font-bold text-secondary dark:text-gray-100">{v.member} Records</h1>
              <p className="text-sm text-muted dark:text-gray-400 mt-1">Progress notes and incident reports</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors"
            >
              <MdAdd className="text-lg" /> Create Record
            </button>
          </div>
          <p className="text-xs text-muted dark:text-gray-400">
            🔒 {v.member} records are locked after submission. Corrections can be added as follow-up notes.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4">
          <RecordFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            students={students}
            onClear={clearFilters}
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {recordsError && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900/50 rounded-lg p-4 flex gap-3">
            <MdError className="text-red-700 dark:text-red-300 text-xl flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-900 dark:text-red-100">
              Failed to load records. Please refresh the page.
            </p>
          </div>
        )}

        {/* Loading state */}
        {recordsLoading && (
          <div className="grid gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-4 h-24 animate-pulse">
                <div className="h-full bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!recordsLoading && records.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted dark:text-gray-400 text-lg mb-4">
              {hasActiveFilters ? 'No records match your filters' : `No ${v.member.toLowerCase()} records found`}
            </p>
            {hasActiveFilters && (
              <p className="text-sm text-muted dark:text-gray-400 mb-6">
                Try clearing filters or adjusting your search.
              </p>
            )}
            <button
              onClick={hasActiveFilters ? clearFilters : () => setShowCreateModal(true)}
              className="text-primary hover:underline font-medium"
            >
              {hasActiveFilters ? 'Clear filters' : 'Create the first record'}
            </button>
          </div>
        )}

        {/* Records list */}
        {!recordsLoading && records.length > 0 && (
          <>
            <div className="grid gap-4 mb-6">
              {records.map(record => (
                <RecordCard
                  key={record._id}
                  record={record}
                  isExpanded={expandedRecords[record._id]}
                  onToggle={() => handleToggleExpand(record._id)}
                  onAddFollowUp={handleAddFollowUp}
                  canAddFollowUp={true}
                />
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-secondary dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  Previous
                </button>
                {Array.from({ length: pages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-primary text-white'
                        : 'border border-gray-300 dark:border-gray-600 text-secondary dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(Math.min(pages, currentPage + 1))}
                  disabled={currentPage === pages}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-secondary dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  Next
                </button>
              </div>
            )}

            {/* Record count */}
            <p className="text-xs text-muted dark:text-gray-400 text-center mt-6">
              Showing {records.length} of {total} records
            </p>
          </>
        )}
      </div>

      {/* Modals */}
      <CreateRecordModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        students={students}
        isLoadingStudents={studentsLoading}
        onSuccess={() => {
          setFilters(prev => ({ ...prev, page: 1 }));
        }}
      />

      {followUpRecordId && (
        <AddFollowUpModal
          isOpen={showFollowUpModal}
          onClose={() => {
            setShowFollowUpModal(false);
            setFollowUpRecordId(null);
          }}
          recordId={followUpRecordId}
        />
      )}
    </div>
  );
}

export default StudentRecords;
